import {useEffect, useMemo, useRef, useState} from 'react';
import {
  CompanyShareSelection,
  IShareTarget,
} from '../../../dto/MessageSharing.dto';
import {useStoreSelector} from '../../../store/configstore';
import {companyIdSelector} from '../../../store/Slices/companySlice';
import {buildShareTargets, hydrateShareSelections} from './buildShareTargets';
import {useDistrictMessageSharingAccess} from './useDistrictMessageSharingAccess';

export interface IMessageVisibilityPayload {
  share_mode: 'explicit_targets';
  targets: IShareTarget[];
}

interface UseMessageVisibilityFormOptions {
  /**
   * @deprecated No longer used — the edit stepper now lets the owner
   * uncheck partners and remove the share inline. The behavior used to
   * block any reduction of existing shares and require the user to open
   * the dedicated Manage Sharing dialog instead. Kept as an option for
   * backward compatibility; it has no effect on current behavior.
   */
  blockClearingExistingShare?: boolean;
}

export const DISTRICT_SHARE_REMOVAL_MESSAGE =
  'To remove existing district sharing, use Manage Sharing from the message details page.';

const buildTargetsKey = (targets: IShareTarget[]): string => {
  return JSON.stringify(
    targets.map(target => ({
      target_type: target.target_type,
      target_company_id: target.target_company_id,
      target_station_id: target.target_station_id ?? null,
      target_company_school_id: target.target_company_school_id ?? null,
      starts_at: target.starts_at,
      ends_at: target.ends_at,
    })),
  );
};

export const buildMessageVisibilityPayload = (
  selections: CompanyShareSelection[],
): IMessageVisibilityPayload | undefined => {
  const targets = buildShareTargets(selections);

  if (!targets.length) {
    return undefined;
  }

  return {
    share_mode: 'explicit_targets',
    targets,
  };
};

export const appendMessageVisibilityToFormData = (
  formData: FormData,
  selections: CompanyShareSelection[],
): void => {
  const visibility = buildMessageVisibilityPayload(selections);

  if (!visibility) {
    return;
  }

  formData.set('visibility[share_mode]', visibility.share_mode);

  visibility.targets.forEach((target, index) => {
    formData.append(
      `visibility[targets][${index}][target_type]`,
      target.target_type,
    );
    formData.append(
      `visibility[targets][${index}][target_company_id]`,
      `${target.target_company_id}`,
    );

    if (typeof target.target_station_id !== 'undefined') {
      formData.append(
        `visibility[targets][${index}][target_station_id]`,
        `${target.target_station_id}`,
      );
    }

    if (typeof target.target_company_school_id !== 'undefined') {
      formData.append(
        `visibility[targets][${index}][target_company_school_id]`,
        `${target.target_company_school_id}`,
      );
    }

    formData.append(
      `visibility[targets][${index}][starts_at]`,
      target.starts_at,
    );
    formData.append(`visibility[targets][${index}][ends_at]`, target.ends_at);
  });
};

export const useMessageVisibilityForm = (
  defaultTargets: IShareTarget[] = [],
  {blockClearingExistingShare = false}: UseMessageVisibilityFormOptions = {},
) => {
  const companyId = useStoreSelector(companyIdSelector);
  const {canManageDistrictSharing} = useDistrictMessageSharingAccess(companyId);
  const showMessageVisibilityEditor = canManageDistrictSharing;
  const defaultTargetsKey = useMemo(
    () => buildTargetsKey(defaultTargets),
    [defaultTargets],
  );
  const hydratedDefaultSelections = useMemo(
    () =>
      hydrateShareSelections(JSON.parse(defaultTargetsKey) as IShareTarget[]),
    [defaultTargetsKey],
  );
  const [shareSelections, setShareSelections] = useState<
    CompanyShareSelection[]
  >(() => hydratedDefaultSelections);

  useEffect(() => {
    setShareSelections(hydratedDefaultSelections);
  }, [hydratedDefaultSelections]);

  // Persistent across stepper re-mounts of MessageVisibilityEditor so the
  // editor's auto-select effect does not re-fire and clobber the user's
  // unchecks when they navigate back to the step.
  const hasAutoSelectedRef = useRef(false);

  const effectiveTargets = useMemo(
    () => buildShareTargets(shareSelections),
    [shareSelections],
  );
  const visibility = useMemo(() => {
    if (!showMessageVisibilityEditor) {
      return undefined;
    }

    return {
      share_mode: 'explicit_targets' as const,
      targets: effectiveTargets,
    };
  }, [effectiveTargets, showMessageVisibilityEditor]);
  const hasBlockedShareRemoval =
    blockClearingExistingShare &&
    showMessageVisibilityEditor &&
    hydratedDefaultSelections.length > 0 &&
    effectiveTargets.length === 0;

  return {
    shareSelections,
    setShareSelections,
    showMessageVisibilityEditor,
    visibility,
    hasBlockedShareRemoval,
    hasAutoSelectedRef,
  };
};
